import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../api/ApiProvider.js';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import { useClassroomSettings } from '../../hooks/useClassroomSettings.js';
import { useClassroomDay } from '../../hooks/useClassroomDay.js';
import { TodayCard } from './TodayCard.js';
import { HomeOlder } from './HomeOlder.js';
import styles from './Home.module.css';

/**
 * Active morning-meeting Home view. Renders just a single TodayCard
 * (greeting + message + "Today we have:" scheduled-announcements
 * section). The classroom name is deliberately NOT shown here — the
 * kids already know which classroom they're in, and the title was
 * crowding the greeting. Navigation lives in the docked chrome
 * rendered at App level.
 */
export function Home() {
  const api = useApi();
  const { active } = useActiveClassroom();

  const settings = useClassroomSettings(active?.id);
  const day = useClassroomDay(active?.id);

  const schedule = useQuery({
    queryKey: ['schedule', active?.id ?? 'none'],
    queryFn: () => api.home.listSchedule(active!.id),
    enabled: active !== null,
  });

  if (!active) {
    return (
      <section className={styles.empty}>
        <p>No classroom selected.</p>
      </section>
    );
  }

  // 4–8 classrooms get the dense To-Do + Turn-In board; everyone else keeps
  // the original mascot-forward single card.
  if (settings.data?.homeVariant === 'g48') {
    return (
      <HomeOlder
        classroomId={active.id}
        settings={settings.data}
        schedule={schedule.data ?? []}
        message={day.data?.message ?? ''}
      />
    );
  }

  return (
    <section className={styles.home} data-testid="home-page">
      <TodayCard
        settings={settings.data ?? null}
        schedule={schedule.data ?? []}
        message={day.data?.message ?? ''}
      />
    </section>
  );
}
